<template>
  <div class="segmented-control" :aria-label="ariaLabel" role="tablist">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="segmented-control__item"
      :class="{ 'segmented-control__item--active': option.value === value }"
      :aria-selected="option.value === value ? 'true' : 'false'"
      role="tab"
      @click="selectOption(option.value)"
    >
      <v-icon v-if="option.icon" small class="segmented-control__icon">
        {{ option.icon }}
      </v-icon>
      <span class="segmented-control__label">{{ option.label }}</span>
      <span
        v-if="option.badge"
        class="segmented-control__badge"
      >
        {{ option.badge }}
      </span>
    </button>
  </div>
</template>

<script>
export default {
  name: "SegmentedControl",
  props: {
    ariaLabel: {
      type: String,
      default: "View options",
    },
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number],
      default: null,
    },
  },
  methods: {
    selectOption(value) {
      this.$emit("input", value);
      this.$emit("change", value);
    },
  },
};
</script>

<style scoped>
.segmented-control {
  align-items: center;
  background: var(--oms-segmented-bg, #edebe5);
  border: 0;
  border-radius: var(--oms-segmented-radius, 0.6875rem);
  display: inline-flex;
  flex-wrap: nowrap !important;
  gap: var(--oms-segmented-gap, 0.25rem);
  justify-content: flex-start;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  padding: var(--oms-segmented-padding, 0.25rem);
  -webkit-overflow-scrolling: touch;
  width: fit-content;
}

.segmented-control__item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: var(--oms-segmented-item-radius, 0.5rem);
  color: var(--oms-muted, #74716a);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto !important;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 0.375rem;
  justify-content: center;
  letter-spacing: 0;
  line-height: 1.2;
  min-height: var(--oms-segmented-item-height, 1.75rem);
  min-width: max-content;
  padding: var(--oms-segmented-item-padding, 0.4375rem 0.625rem);
  text-align: center;
  text-transform: none;
  transition:
    background 0.16s ease,
    box-shadow 0.16s ease,
    color 0.16s ease;
  white-space: nowrap !important;
}

.segmented-control__item:hover,
.segmented-control__item:focus {
  background: var(--oms-segmented-hover-bg, var(--oms-surface, #ffffff));
  color: var(--oms-ink-strong, #1a1916);
  outline: none;
}

.segmented-control__item:focus {
  box-shadow: 0 0 0 0.1875rem rgba(234, 122, 28, 0.16);
}

.segmented-control__item--active {
  background: var(--oms-segmented-active-bg, var(--oms-surface, #ffffff));
  box-shadow: 0 0.0625rem 0.125rem rgba(26, 25, 22, 0.13);
  color: var(--oms-ink-strong, #1a1916);
}

.segmented-control__label {
  min-width: 0;
  white-space: nowrap;
}

.segmented-control__icon {
  color: currentColor !important;
  flex: 0 0 auto;
  font-size: 1rem !important;
}

.segmented-control__badge {
  align-items: center;
  background: var(--oms-surface-soft, #faf9f6);
  border: 0.0625rem solid var(--oms-border, #ebe9e2);
  border-radius: 999rem;
  color: var(--oms-ink-strong, #1a1916);
  display: inline-flex;
  flex: 0 0 auto;
  font-family: "Geist Mono", ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.6875rem;
  font-weight: 600;
  justify-content: center;
  line-height: 1;
  min-width: 1.25rem;
  padding: 0.1875rem 0.375rem;
}

@media (max-width: 37.5rem) {
  .segmented-control {
    width: 100%;
  }

  .segmented-control__item {
    flex: 0 0 auto !important;
  }
}
</style>
